import { Link, useLocation } from "react-router-dom";
import { Bitcoin, Shield, LogOut, User, Settings, ChevronDown, Menu, X, Bookmark, Moon, Sun, Wallet, Briefcase, LayoutDashboard, Loader2 } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useUserRoles } from "@/hooks/useUserRoles";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { useTheme } from "@/hooks/useTheme";
import { Database } from "@/integrations/supabase/types";
import { Session } from "@supabase/supabase-js";
import { NotificationCenter } from "@/components/NotificationCenter";
import { GlobalSearch } from "@/components/GlobalSearch";
import { Badge } from "@/components/ui/badge";

type AppRole = Database["public"]["Enums"]["app_role"];
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Separator } from "@/components/ui/separator";

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: userRoles = [] } = useUserRoles();
  const { theme, toggleTheme } = useTheme();
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const hasRole = (role: AppRole) => userRoles.includes(role);
  const isAdmin = hasRole('admin');
  const isOrganizer = hasRole('event_organizer');
  const isEmployer = hasRole('employer');
  const isJobSeeker = hasRole('job_seeker');
  
  const isActive = (path: string) => location.pathname === path;

  useEffect(() => {
    // Set up auth listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state changed:', event, session?.user?.email);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });
    
    // Then check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Initial session check:', session?.user?.email);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });
    
    return () => subscription.unsubscribe();
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    toast.success("Logged out successfully");
    navigate("/");
    setMobileMenuOpen(false);
  };

  const handleMobileNavigation = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };
  
  return (
    <nav className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-lg">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 group shrink-0">
            <Bitcoin className="h-8 w-8 text-primary group-hover:animate-glow" />
            <span className="text-xl font-bold glow-text">BitcoinMind</span>
            <Badge variant="secondary" className="ml-2 text-xs px-2 py-0.5 animate-pulse">
              BETA
            </Badge>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <Link 
              to="/" 
              className={`text-sm font-medium transition-colors hover:text-primary ${isActive('/') ? 'text-primary' : 'text-foreground'}`}
            >
              Home
            </Link>
            <Link 
              to="/news" 
              className={`text-sm font-medium transition-colors hover:text-primary ${isActive('/news') ? 'text-primary' : 'text-foreground'}`}
            >
              News
            </Link>
            
            {/* Market Dropdown */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className={`text-sm font-medium ${isActive('/market') || isActive('/tools') || isActive('/top-holders') ? 'text-primary' : 'text-foreground'}`}>
                    Market
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="bg-background border border-border">
                    <ul className="w-48 p-2">
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/market"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            Market Analysis
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/tools"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            Tools
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/top-holders"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            Top Holders
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/wallet-selector"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            Wallet Selector
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/bitcoin-lenders"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            Bitcoin Lenders
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/bitcoin-cards"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            Bitcoin Cards
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/mining-calculator"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            Mining Calculator
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/mining-dashboard"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            Mining Dashboard
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/lightning"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            Lightning Network
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/treasuries"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            Bitcoin Treasuries
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/dca-calculator"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            DCA Calculator
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/onchain-metrics"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            On-Chain Metrics
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/price-analysis"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            Price Analysis
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            
            {/* Events Dropdown */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className={`text-sm font-medium ${isActive('/events') || isActive('/calendar') || isActive('/events-pricing') ? 'text-primary' : 'text-foreground'}`}>
                    Events
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="bg-background border border-border">
                    <ul className="w-48 p-2">
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/events"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            Browse Events
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/calendar"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            Calendar
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/events-pricing"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            Pricing
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            
            {/* Community Dropdown */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className={`text-sm font-medium ${isActive('/sentiment') || isActive('/influencers') || isActive('/predict') || isActive('/bitcoin-iq') ? 'text-primary' : 'text-foreground'}`}>
                    Community
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="bg-background border border-border">
                    <ul className="w-52 p-2">
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/bitcoin-iq"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors flex items-center gap-2"
                          >
                            Bitcoin IQ Quiz
                            <Badge variant="outline" className="text-[10px] px-1 py-0 bg-primary/10 text-primary border-primary/30">NEW</Badge>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/sentiment"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            Sentiment Board
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/predict"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            Predict the Market
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/influencers"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            Influencer Leaderboard
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            
            <Link 
              to="/jobs" 
              className={`text-sm font-medium transition-colors hover:text-primary ${isActive('/jobs') ? 'text-primary' : 'text-foreground'}`}
            >
              Jobs
            </Link>
            <Link 
              to="/learn" 
              className={`text-sm font-medium transition-colors hover:text-primary ${isActive('/learn') ? 'text-primary' : 'text-foreground'}`}
            >
              Learn
            </Link>
            <Link 
              to="/courses" 
              className={`text-sm font-medium transition-colors hover:text-primary flex items-center gap-1 ${isActive('/courses') ? 'text-primary' : 'text-foreground'}`}
            >
              Academy
              <Badge variant="outline" className="text-[10px] px-1 py-0 bg-primary/10 text-primary border-primary/30">NEW</Badge>
            </Link>
            
            {/* More Dropdown */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className={`text-sm font-medium ${isActive('/newsletter') || isActive('/about') || isActive('/about-bitcoin') || isActive('/contact') || isActive('/advertise') ? 'text-primary' : 'text-foreground'}`}>
                    More
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="bg-background border border-border">
                    <ul className="w-52 p-2">
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/newsletter"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            Newsletter
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <Separator className="my-2" />
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/about"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            About
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/about-bitcoin"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            About Bitcoin
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/contact"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            Get in Touch
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link 
                            to="/advertise"
                            className="block px-4 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                          >
                            Advertise With Us
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            
            {/* Dashboards Dropdown */}
            {(isAdmin || isOrganizer || isEmployer || isJobSeeker) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-1">
                    <LayoutDashboard className="h-4 w-4" />
                    Dashboards
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-popover z-50">
                  <DropdownMenuLabel>Your Dashboards</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2 cursor-pointer">
                        <Shield className="h-4 w-4" />
                        Admin Dashboard
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {isOrganizer && (
                    <DropdownMenuItem asChild>
                      <Link to="/organizer" className="flex items-center gap-2 cursor-pointer">
                        <Briefcase className="h-4 w-4" />
                        Event Organizer
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {isEmployer && (
                    <DropdownMenuItem asChild>
                      <Link to="/employer" className="flex items-center gap-2 cursor-pointer">
                        <Briefcase className="h-4 w-4" />
                        Employer Dashboard
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {isJobSeeker && (
                    <DropdownMenuItem asChild>
                      <Link to="/jobs/my-applications" className="flex items-center gap-2 cursor-pointer">
                        <Wallet className="h-4 w-4" />
                        My Applications
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/request-role" className="flex items-center gap-2 cursor-pointer">
                      <LayoutDashboard className="h-4 w-4" />
                      Request New Role
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            
            <GlobalSearch />
            
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="ml-2"
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            >
              {theme === "dark" ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </Button>
            
            {user && <NotificationCenter />}
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="ml-2">
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-background border border-border z-50">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">Account</p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer">
                    <User className="mr-2 h-4 w-4" />
                    <span>Dashboard</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/saved-articles")} className="cursor-pointer">
                    <Bookmark className="mr-2 h-4 w-4" />
                    <span>Saved Articles</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/portfolio")} className="cursor-pointer">
                    <Wallet className="mr-2 h-4 w-4" />
                    <span>Portfolio Tracker</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/job-seeker/applications")} className="cursor-pointer">
                    <Briefcase className="mr-2 h-4 w-4" />
                    <span>My Applications</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/settings")} className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth" className="ml-4">
                <Button variant="outline" size="sm">
                  Sign In
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className="relative">
                {loading ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <>
                    <Menu className="h-6 w-6" />
                    {user && (
                      <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-primary animate-pulse" />
                    )}
                  </>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-background p-0">
              <ScrollArea className="h-full">
                <div className="flex flex-col h-full p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Bitcoin className="h-6 w-6 text-primary" />
                      <span className="text-lg font-bold">BitcoinMind</span>
                    </div>
                  </div>

                {/* Mobile Search */}
                <div className="mb-4">
                  <GlobalSearch alwaysExpanded />
                </div>

                <nav className="flex flex-col space-y-1 flex-1">
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/")}
                  >
                    Home
                  </Button>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/news")}
                  >
                    News
                  </Button>
                  
                  <Separator className="my-2" />
                  
                  <div className="px-3 py-2">
                    <p className="text-xs font-semibold text-muted-foreground">MARKET</p>
                  </div>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/market")}
                  >
                    Market Analysis
                  </Button>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/tools")}
                  >
                    Tools
                  </Button>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/top-holders")}
                  >
                    Top Holders
                  </Button>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/wallet-selector")}
                  >
                    Wallet Selector
                  </Button>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/mining-calculator")}
                  >
                    Mining Calculator
                  </Button>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/lightning")}
                  >
                    Lightning Network
                  </Button>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/treasuries")}
                  >
                    Bitcoin Treasuries
                  </Button>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/dca-calculator")}
                  >
                    DCA Calculator
                  </Button>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/onchain-metrics")}
                  >
                    On-Chain Metrics
                  </Button>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/price-analysis")}
                  >
                    Price Analysis
                  </Button>
                  
                  <Separator className="my-2" />
                  
                  <div className="px-3 py-2">
                    <p className="text-xs font-semibold text-muted-foreground">EVENTS</p>
                  </div>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/events")}
                  >
                    Browse Events
                  </Button>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/calendar")}
                  >
                    Calendar
                  </Button>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/events-pricing")}
                  >
                    Pricing
                  </Button>
                  
                  <Separator className="my-2" />
                  
                  <div className="px-3 py-2">
                    <p className="text-xs font-semibold text-muted-foreground">COMMUNITY</p>
                  </div>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/bitcoin-iq")}
                  >
                    Bitcoin IQ Quiz <Badge variant="outline" className="ml-2 text-[10px] px-1 py-0 bg-primary/10 text-primary border-primary/30">NEW</Badge>
                  </Button>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/sentiment")}
                  >
                    Sentiment Board
                  </Button>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/predict")}
                  >
                    Predict Market
                  </Button>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/influencers")}
                  >
                    Influencer Leaderboard
                  </Button>
                  
                  <Separator className="my-2" />
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/jobs")}
                  >
                    Jobs
                  </Button>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/learn")}
                  >
                    Learn
                  </Button>
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/courses")}
                  >
                    Academy <Badge variant="outline" className="ml-2 text-[10px] px-1 py-0 bg-primary/10 text-primary border-primary/30">NEW</Badge>
                  </Button>
                  
                  <Separator className="my-2" />
                  
                  <div className="px-3 py-2">
                    <p className="text-xs font-semibold text-muted-foreground">MORE</p>
                  </div>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/newsletter")}
                  >
                    Newsletter
                  </Button>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/about")}
                  >
                    About
                  </Button>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/about-bitcoin")}
                  >
                    About Bitcoin
                  </Button>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/contact")}
                  >
                    Get in Touch
                  </Button>
                  
                  <Button
                    variant="ghost"
                    className="justify-start text-base"
                    onClick={() => handleMobileNavigation("/advertise")}
                  >
                    Advertise With Us
                  </Button>

                  {/* Dashboards Section */}
                  {(isAdmin || isOrganizer || isEmployer || isJobSeeker) && (
                    <>
                      <Separator className="my-2" />
                      <div className="space-y-1">
                        <div className="px-3 py-2 text-xs font-semibold text-muted-foreground">DASHBOARDS</div>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            className="justify-start w-full text-base"
                            onClick={() => handleMobileNavigation("/admin")}
                          >
                            <Shield className="mr-2 h-4 w-4" />
                            Admin Dashboard
                          </Button>
                        )}
                        {isOrganizer && (
                          <Button
                            variant="ghost"
                            className="justify-start w-full text-base"
                            onClick={() => handleMobileNavigation("/organizer")}
                          >
                            <Briefcase className="mr-2 h-4 w-4" />
                            Event Organizer
                          </Button>
                        )}
                        {isEmployer && (
                          <Button
                            variant="ghost"
                            className="justify-start w-full text-base"
                            onClick={() => handleMobileNavigation("/employer")}
                          >
                            <Briefcase className="mr-2 h-4 w-4" />
                            Employer Dashboard
                          </Button>
                        )}
                        {isJobSeeker && (
                          <Button
                            variant="ghost"
                            className="justify-start w-full text-base"
                            onClick={() => handleMobileNavigation("/jobs/my-applications")}
                          >
                            <Wallet className="mr-2 h-4 w-4" />
                            My Applications
                          </Button>
                        )}
                      </div>
                      <Separator className="my-2" />
                      <Button
                        variant="ghost"
                        className="justify-start w-full text-base"
                        onClick={() => handleMobileNavigation("/request-role")}
                      >
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        Request New Role
                      </Button>
                    </>
                  )}
                </nav>

                <div className="pt-4 border-t border-border space-y-4">
                  <Button
                    variant="ghost"
                    className="justify-start w-full"
                    onClick={toggleTheme}
                  >
                    {theme === "dark" ? (
                      <>
                        <Sun className="mr-2 h-4 w-4" />
                        Light Mode
                      </>
                    ) : (
                      <>
                        <Moon className="mr-2 h-4 w-4" />
                        Dark Mode
                      </>
                    )}
                  </Button>

                  {loading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : user ? (
                    <div className="space-y-2">
                      <div className="px-3 py-2 text-sm bg-muted/50 rounded-md">
                        <p className="text-xs text-muted-foreground mb-1">Signed in as</p>
                        <p className="font-medium truncate">{user.email}</p>
                      </div>
                      <Button
                        variant="ghost"
                        className="justify-start w-full"
                        onClick={() => handleMobileNavigation("/profile")}
                      >
                        <User className="mr-2 h-4 w-4" />
                        Dashboard
                      </Button>
                      <Button
                        variant="ghost"
                        className="justify-start w-full"
                        onClick={() => handleMobileNavigation("/saved-articles")}
                      >
                        <Bookmark className="mr-2 h-4 w-4" />
                        Saved Articles
                      </Button>
                      <Button
                        variant="ghost"
                        className="justify-start w-full"
                        onClick={() => handleMobileNavigation("/portfolio")}
                      >
                        <Wallet className="mr-2 h-4 w-4" />
                        Portfolio Tracker
                      </Button>
                      <Button
                        variant="ghost"
                        className="justify-start w-full"
                        onClick={() => handleMobileNavigation("/settings")}
                      >
                        <Settings className="mr-2 h-4 w-4" />
                        Settings
                      </Button>
                      <Button
                        variant="ghost"
                        className="justify-start w-full text-destructive"
                        onClick={handleLogout}
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        Log out
                      </Button>
                    </div>
                  ) : (
                    <Button
                      className="w-full"
                      onClick={() => handleMobileNavigation("/auth")}
                    >
                      Sign In
                    </Button>
                  )}
                </div>
                </div>
              </ScrollArea>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
